'use client'

import { useState } from 'react'

/**
 * Copy-credit button — copies a pre-formatted credit line to the user's
 * clipboard and shows a 2-second "Copied!" confirmation. Used on the
 * /press page next to each downloadable photo so journalists can grab
 * the attribution line without retyping.
 *
 * Format follows the brief's design finding #5: combine creator + credit
 * + copyright into one runnable line. Empty pieces are skipped so the
 * line stays clean for photos with partial attribution.
 */
export function CopyCreditButton({ creditLine }: { creditLine: string }) {
  const [copied, setCopied] = useState(false)

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(creditLine)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Older browsers / iframe contexts where clipboard API is blocked.
      // Fall back to the selection trick — silent on failure rather than
      // surfacing a toast for a tertiary action.
      const ta = document.createElement('textarea')
      ta.value = creditLine
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try { document.execCommand('copy') } catch { /* noop */ }
      document.body.removeChild(ta)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <button
      type="button"
      onClick={onCopy}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--color-cta-readable)] hover:underline"
      aria-label="Copy credit line to clipboard"
    >
      {copied ? 'Copied!' : 'Copy credit'}
    </button>
  )
}
